import { useEffect, useRef } from 'react'

declare global {
  interface Window {
    google: any
    googleSignInCallback: (response: any) => void
  }
}

interface GoogleLoginProps {
  onSuccess: (user: GoogleUser) => void
  onError: (error: any) => void
}

export interface GoogleUser {
  id: string
  name: string
  email: string
  picture: string
  given_name: string
  family_name: string
}

const GoogleLogin: React.FC<GoogleLoginProps> = ({ onSuccess, onError }) => {
  const googleButtonRef = useRef<HTMLDivElement>(null)

  // Google OAuth Client ID - 需要在Google Cloud Console中配置
  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

  // 如果没有配置Client ID，显示配置提示
  if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID === 'your_google_client_id_here') {
    return (
      <div className="w-full p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="text-sm text-yellow-800">
          <div className="font-medium mb-2">需要配置 Google OAuth</div>
          <div className="text-xs space-y-1">
            <p>1. 在 Google Cloud Console 创建 OAuth 客户端 ID</p>
            <p>2. 创建 .env.local 文件并添加：</p>
            <code className="block bg-yellow-100 p-2 rounded mt-1">
              VITE_GOOGLE_CLIENT_ID=你的客户端ID
            </code>
            <p>3. 重启开发服务器</p>
          </div>
        </div>
      </div>
    )
  }

  useEffect(() => {
    // 等待Google脚本加载完成后初始化
    const checkGoogleScript = () => {
      if (window.google) {
        initializeGoogleSignIn()
      } else {
        // 如果Google脚本还没加载完成，等待一段时间后重试
        setTimeout(checkGoogleScript, 100)
      }
    }
    
    checkGoogleScript()
  }, [])

  const initializeGoogleSignIn = () => {
    if (window.google) {
      // 设置全局回调函数
      window.googleSignInCallback = handleCredentialResponse

      // 初始化Google Identity Services
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      })

      // 渲染登录按钮
      if (googleButtonRef.current) {
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: '100%',
        })
      }

      // 显示One Tap提示（可选）
      // window.google.accounts.id.prompt()
    }
  }

  const handleCredentialResponse = (response: any) => {
    try {
      // 解码JWT token
      const token = response.credential
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )

      const userData = JSON.parse(jsonPayload)
      console.log('Google login success:', userData)

      const user: GoogleUser = {
        id: userData.sub,
        name: userData.name,
        email: userData.email,
        picture: userData.picture,
        given_name: userData.given_name,
        family_name: userData.family_name,
      }

      onSuccess(user)
    } catch (error) {
      console.error('Error processing Google login:', error)
      onError(error)
    }
  }

  return (
    <div className="w-full">
      <div ref={googleButtonRef} className="w-full"></div>
    </div>
  )
}

export default GoogleLogin 